/**
 * Promo Codes Management Component
 * Admin dashboard for managing marketing promo codes
 * Features: CRUD, Analytics, Expiring Alerts, Bulk Import, CSV Export
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Tag, Plus, Trash2, Edit2, Users, TrendingUp, 
  Calendar, Percent, DollarSign, CheckCircle, XCircle, Loader2,
  Download, Upload, AlertTriangle, BarChart3, Clock
} from "lucide-react";
import { authAxios } from "@/utils/auth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

export default function PromoCodesManagement() {
  const [promoCodes, setPromoCodes] = useState([]);
  const [stats, setStats] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [expiringCodes, setExpiringCodes] = useState([]);
  const [scheduledCodes, setScheduledCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState("codes");
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    code: "",
    description: "",
    discount_type: "fixed",
    discount_value: 50,
    max_uses: null,
    max_uses_per_customer: 1,
    starts_at: null,
    expires_at: null,
    is_active: true,
  });

  const [importData, setImportData] = useState("");
  const [importing, setImporting] = useState(false);

  const fetchPromoCodes = useCallback(async () => {
    setLoading(true);
    try {
      const [codesRes, statsRes, analyticsRes, expiringRes, scheduledRes] = await Promise.all([
        authAxios.get("/promo-codes"),
        authAxios.get("/promo-codes/stats"),
        authAxios.get("/promo-codes/analytics"),
        authAxios.get("/promo-codes/expiring?days=7"),
        authAxios.get("/promo-codes/scheduled")
      ]);
      setPromoCodes(codesRes.data);
      setStats(statsRes.data);
      setAnalytics(analyticsRes.data);
      setExpiringCodes(expiringRes.data);
      setScheduledCodes(scheduledRes.data);
    } catch {
      toast.error("Failed to load promo codes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPromoCodes();
  }, [fetchPromoCodes]);

  const resetForm = () => {
    setFormData({
      code: "",
      description: "",
      discount_type: "fixed",
      discount_value: 50,
      max_uses: null,
      max_uses_per_customer: 1,
      starts_at: null,
      expires_at: null,
      is_active: true,
    });
    setEditingCode(null);
  };

  const handleCreate = async () => {
    if (!formData.code || !formData.description) {
      toast.error("Please fill in code and description");
      return;
    }

    setSaving(true);
    try {
      await authAxios.post("/promo-codes", formData);
      toast.success("Promo code created!");
      setShowCreateModal(false);
      resetForm();
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create promo code");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.code || !formData.description) {
      toast.error("Please fill in code and description");
      return;
    }

    setSaving(true);
    try {
      await authAxios.put(`/promo-codes/${editingCode}`, formData);
      toast.success("Promo code updated!");
      setShowCreateModal(false);
      resetForm();
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update promo code");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Delete promo code "${code}"?`)) return;

    try {
      await authAxios.delete(`/promo-codes/${code}`);
      toast.success("Promo code deleted!");
      fetchPromoCodes();
    } catch {
      toast.error("Failed to delete promo code");
    }
  };

  const handleToggleActive = async (promo) => {
    try {
      await authAxios.put(`/promo-codes/${promo.code}`, {
        ...promo,
        is_active: !promo.is_active,
      });
      toast.success(`Promo code ${promo.is_active ? 'deactivated' : 'activated'}!`);
      fetchPromoCodes();
    } catch {
      toast.error("Failed to update promo code");
    }
  };

  const openEditModal = (promo) => {
    setFormData({
      code: promo.code,
      description: promo.description,
      discount_type: promo.discount_type,
      discount_value: promo.discount_value,
      max_uses: promo.max_uses,
      max_uses_per_customer: promo.max_uses_per_customer,
      starts_at: promo.starts_at ? new Date(promo.starts_at).toISOString().split('T')[0] : null,
      expires_at: promo.expires_at ? new Date(promo.expires_at).toISOString().split('T')[0] : null,
      is_active: promo.is_active,
    });
    setEditingCode(promo.code);
    setShowCreateModal(true);
  };

  // Export CSV
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await authAxios.get("/promo-codes/export", {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `promo_codes_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success("CSV exported successfully!");
    } catch {
      toast.error("Failed to export CSV");
    } finally {
      setExporting(false);
    }
  };

  // Bulk Import
  const handleBulkImport = async () => {
    if (!importData.trim()) {
      toast.error("Please enter promo codes to import");
      return;
    }

    setImporting(true);
    try {
      // Parse CSV-like input: CODE,DESCRIPTION,TYPE,VALUE
      const lines = importData.trim().split('\n');
      const codes = [];
      
      for (const line of lines) {
        const parts = line.split(',').map(p => p.trim());
        if (parts.length >= 2) {
          codes.push({
            code: parts[0],
            description: parts[1],
            discount_type: parts[2] || 'fixed',
            discount_value: parseFloat(parts[3]) || 50,
            max_uses: parts[4] ? parseInt(parts[4]) : null,
            max_uses_per_customer: parts[5] ? parseInt(parts[5]) : 1,
            is_active: true
          });
        }
      }

      if (codes.length === 0) {
        toast.error("No valid codes found. Format: CODE,DESCRIPTION,TYPE,VALUE");
        setImporting(false);
        return;
      }

      const response = await authAxios.post("/promo-codes/bulk-import", codes);
      
      if (response.data.created_count > 0) {
        toast.success(`Imported ${response.data.created_count} promo codes!`);
      }
      if (response.data.error_count > 0) {
        toast.warning(`${response.data.error_count} codes failed: ${response.data.errors.map(e => e.code).join(', ')}`);
      }
      
      setShowImportModal(false);
      setImportData("");
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import promo codes");
    } finally {
      setImporting(false);
    }
  };

  // Calculate summary stats
  const totalUses = stats.reduce((sum, s) => sum + (s.current_uses || 0), 0);
  const activeCount = stats.filter(s => s.is_active).length;

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-4 gap-4">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div data-testid="promo-codes-management">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
          Promo Codes
        </h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportCSV} disabled={exporting} data-testid="export-csv-btn">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
            Export CSV
          </Button>
          <Button variant="outline" onClick={() => setShowImportModal(true)} data-testid="bulk-import-btn">
            <Upload className="w-4 h-4 mr-2" />
            Bulk Import
          </Button>
          <Button onClick={() => { resetForm(); setShowCreateModal(true); }} data-testid="create-promo-code-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Code
          </Button>
        </div>
      </div>

      {/* Expiring Codes Alert */}
      {expiringCodes.length > 0 && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="expiring-alert">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <span className="font-semibold text-amber-800">Codes Expiring Soon</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {expiringCodes.map(code => (
              <Badge key={code.code} variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                <Clock className="w-3 h-3 mr-1" />
                {code.code} - {code.days_left} day{code.days_left !== 1 ? 's' : ''} left
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Scheduled Codes Alert */}
      {scheduledCodes.length > 0 && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid="scheduled-alert">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            <span className="font-semibold text-blue-800">Scheduled Codes (Not Yet Active)</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {scheduledCodes.map(code => (
              <Badge key={code.code} variant="outline" className="bg-blue-100 text-blue-800 border-blue-300">
                <Clock className="w-3 h-3 mr-1" />
                {code.code} - activates in {code.days_until_active} day{code.days_until_active !== 1 ? 's' : ''}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Tag className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Codes</p>
                <p className="text-2xl font-bold" data-testid="stat-total-codes">{promoCodes.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Active Codes</p>
                <p className="text-2xl font-bold" data-testid="stat-active-codes">{activeCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Uses</p>
                <p className="text-2xl font-bold" data-testid="stat-total-uses">{totalUses}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Discounts Given</p>
                <p className="text-2xl font-bold" data-testid="stat-total-discounts">
                  ${analytics?.summary?.total_discount_given?.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs: Codes List vs Analytics */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="codes" className="flex items-center gap-2">
            <Tag className="w-4 h-4" />
            All Codes
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Analytics
          </TabsTrigger>
        </TabsList>

        {/* Codes List Tab */}
        <TabsContent value="codes">
          <Card>
            <CardHeader>
              <CardTitle>All Promo Codes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 font-medium text-slate-600">Code</th>
                      <th className="text-left p-3 font-medium text-slate-600">Description</th>
                      <th className="text-left p-3 font-medium text-slate-600">Discount</th>
                      <th className="text-left p-3 font-medium text-slate-600">Uses</th>
                      <th className="text-left p-3 font-medium text-slate-600">Status</th>
                      <th className="text-left p-3 font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.map((promo) => (
                      <tr key={promo.code} className="border-b hover:bg-slate-50" data-testid={`promo-row-${promo.code}`}>
                        <td className="p-3">
                          <code className="px-2 py-1 bg-slate-100 rounded text-sm font-mono font-bold">
                            {promo.code}
                          </code>
                        </td>
                        <td className="p-3 text-slate-600 text-sm">{promo.description}</td>
                        <td className="p-3" data-testid={`promo-discount-${promo.code}`}>
                          <Badge variant={promo.discount_type === "percentage" ? "secondary" : "default"}>
                            {promo.discount_type === "percentage" ? (
                              <><Percent className="w-3 h-3 mr-1" />{promo.discount_value}%</>
                            ) : (
                              <><DollarSign className="w-3 h-3 mr-1" />{promo.discount_value}</>
                            )}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-slate-400" />
                            <span>{promo.current_uses || 0}</span>
                            {promo.max_uses && (
                              <span className="text-slate-400">/ {promo.max_uses}</span>
                            )}
                          </div>
                        </td>
                        <td className="p-3" data-testid={`promo-status-${promo.code}`}>
                          {promo.is_scheduled ? (
                            <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300">
                              <Calendar className="w-3 h-3 mr-1" />
                              Scheduled
                            </Badge>
                          ) : (
                            <Badge variant={promo.is_active ? "default" : "secondary"} 
                                   className={promo.is_active ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-600"}>
                              {promo.is_active ? "Active" : "Inactive"}
                            </Badge>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openEditModal(promoCodes.find(p => p.code === promo.code))}
                              data-testid={`edit-promo-${promo.code}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleToggleActive(promoCodes.find(p => p.code === promo.code))}
                              data-testid={`toggle-promo-${promo.code}`}
                            >
                              {promo.is_active ? <XCircle className="w-4 h-4 text-red-500" /> : <CheckCircle className="w-4 h-4 text-green-500" />}
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDelete(promo.code)}
                              data-testid={`delete-promo-${promo.code}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics">
          <div className="space-y-6">
            {/* Analytics Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-6">
                  <p className="text-sm text-slate-500 mb-1">Quotes with Promo Codes</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {analytics?.summary?.quotes_with_promo || 0}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <p className="text-sm text-slate-500 mb-1">Total Revenue (with Promo)</p>
                  <p className="text-3xl font-bold text-emerald-600">
                    ${analytics?.summary?.total_revenue_with_promo?.toLocaleString() || 0}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <p className="text-sm text-slate-500 mb-1">Total Discounts Given</p>
                  <p className="text-3xl font-bold text-purple-600">
                    ${analytics?.summary?.total_discount_given?.toLocaleString() || 0}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Top Performing Codes */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Top Performing Promo Codes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 font-medium text-slate-600">Rank</th>
                        <th className="text-left p-3 font-medium text-slate-600">Code</th>
                        <th className="text-left p-3 font-medium text-slate-600">Description</th>
                        <th className="text-left p-3 font-medium text-slate-600">Uses</th>
                        <th className="text-left p-3 font-medium text-slate-600">Unique Customers</th>
                        <th className="text-left p-3 font-medium text-slate-600">Revenue Generated</th>
                        <th className="text-left p-3 font-medium text-slate-600">Discounts Given</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics?.top_performing_codes?.map((code, index) => (
                        <tr key={code.code} className="border-b hover:bg-slate-50">
                          <td className="p-3">
                            <Badge variant={index < 3 ? "default" : "outline"} className={index === 0 ? "bg-yellow-500" : index === 1 ? "bg-slate-400" : index === 2 ? "bg-amber-600" : ""}>
                              #{index + 1}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <code className="px-2 py-1 bg-slate-100 rounded text-sm font-mono font-bold">
                              {code.code}
                            </code>
                          </td>
                          <td className="p-3 text-slate-600 text-sm">{code.description}</td>
                          <td className="p-3 font-medium">{code.uses}</td>
                          <td className="p-3">{code.unique_customers}</td>
                          <td className="p-3 text-emerald-600 font-medium">
                            ${code.total_revenue_generated?.toLocaleString() || 0}
                          </td>
                          <td className="p-3 text-purple-600">
                            ${code.total_discount_given?.toLocaleString() || 0}
                          </td>
                        </tr>
                      ))}
                      {(!analytics?.top_performing_codes || analytics.top_performing_codes.length === 0) && (
                        <tr>
                          <td colSpan={7} className="p-8 text-center text-slate-500">
                            No promo code usage data yet. Start promoting your codes!
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCode ? "Edit Promo Code" : "Create Promo Code"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Code</Label>
              <Input
                placeholder="SHINE50"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                disabled={!!editingCode}
                data-testid="promo-code-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                placeholder="Spring promotion - $50 off"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                data-testid="promo-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Discount Type</Label>
                <Select 
                  value={formData.discount_type} 
                  onValueChange={(v) => setFormData({ ...formData, discount_type: v })}
                  data-testid="promo-discount-type-select"
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Discount Value</Label>
                <Input
                  type="number"
                  value={formData.discount_value}
                  onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) || 0 })}
                  data-testid="promo-discount-value-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Max Total Uses</Label>
                <Input
                  type="number"
                  placeholder="Unlimited"
                  value={formData.max_uses || ""}
                  onChange={(e) => setFormData({ ...formData, max_uses: e.target.value ? parseInt(e.target.value) : null })}
                />
              </div>

              <div className="space-y-2">
                <Label>Uses Per Customer</Label>
                <Input
                  type="number"
                  value={formData.max_uses_per_customer}
                  onChange={(e) => setFormData({ ...formData, max_uses_per_customer: parseInt(e.target.value) || 1 })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date (Optional)</Label>
                <Input
                  type="date"
                  value={formData.starts_at || ""}
                  onChange={(e) => setFormData({ ...formData, starts_at: e.target.value || null })}
                  data-testid="promo-starts-at-input"
                />
                <p className="text-xs text-slate-500">Schedule code to activate on a future date</p>
              </div>

              <div className="space-y-2">
                <Label>Expiration Date (Optional)</Label>
                <Input
                  type="date"
                  value={formData.expires_at || ""}
                  onChange={(e) => setFormData({ ...formData, expires_at: e.target.value || null })}
                  data-testid="promo-expires-at-input"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label>Active</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateModal(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={editingCode ? handleUpdate : handleCreate} disabled={saving} data-testid="promo-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingCode ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Bulk Import Promo Codes</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
              <p className="font-medium mb-2">CSV Format:</p>
              <code className="block bg-slate-200 p-2 rounded text-xs">
                CODE,DESCRIPTION,TYPE,VALUE,MAX_USES,PER_CUSTOMER
              </code>
              <p className="mt-2 text-xs">
                Example: SUMMER25,Summer Sale - 25% off,percentage,25,,1
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Paste CSV Data</Label>
              <textarea
                className="w-full h-40 p-3 border rounded-lg text-sm font-mono"
                placeholder="PROMO1,Description 1,fixed,50&#10;PROMO2,Description 2,percentage,10"
                value={importData}
                onChange={(e) => setImportData(e.target.value)}
                data-testid="bulk-import-textarea"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowImportModal(false); setImportData(""); }}>
              Cancel
            </Button>
            <Button onClick={handleBulkImport} disabled={importing} data-testid="bulk-import-submit-btn">
              {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Import Codes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
